import _get from 'lodash/get';
import { createSelector } from 'reselect';

import * as authActions from 'modules/auth/actions';
import { createReducer } from 'utils/reducer';

import * as mfaActions from './actions';
import * as helpers from './reducerHelpers';
import INITIAL_STATE from './initialState';

/**
 * Build the map of 'mfa' redux handlers.
 *
 * @type {Object}
 */
const mfa = {
    [authActions.logout.SUCCESS]: helpers.clearMFASession,
    [mfaActions.clearMFAEnrollmentError.TRIGGER]: helpers.clearMFAEnrollmentError,
    [mfaActions.clearMFAError.TRIGGER]: helpers.clearMFAError,
    [mfaActions.clearMFASession.TRIGGER]: helpers.clearMFASession,
    [mfaActions.completeMFAEnrollment.REQUEST]: helpers.completeMFAEnrollmentRequest,
    [mfaActions.registerMFAMethod.ERROR]: helpers.registerMFAMethodError,
    [mfaActions.registerMFAMethod.REQUEST]: helpers.registerMFAMethodRequest,
    [mfaActions.registerMFAMethod.SUCCESS]: helpers.registerMFAMethodSuccess,
    [mfaActions.setMFASession.TRIGGER]: helpers.setMFASession,
    [mfaActions.verifyMFACode.ERROR]: helpers.verifyMFACodeError,
    [mfaActions.verifyMFACode.REQUEST]: helpers.verifyMFACodeRequest,
    [mfaActions.verifyMFACode.SUCCESS]: helpers.verifyMFACodeSuccess,
};

// Create and export the 'mfa' reducer.
// NOTE: The default export will be the reducer.
export default createReducer(INITIAL_STATE, mfa);

// SELECTORS

/**
 * Get the complete 'mfa' state.
 *
 * @method getMFA
 * @param  {Object} state The app state
 * @return {Object}       The mfa state
 */
export const getMFA = state => _get(state, 'mfa', INITIAL_STATE);

/**
 * Get the short lived MFA token.
 *
 * @method getMFAToken
 * @return {string} The mfa token
 */
export const getMFAToken = createSelector([getMFA], mfaState => _get(mfaState, 'mfaToken', ''));

/**
 * Get the MFA method the user has enrolled.
 *
 * @method getMFAMethod
 * @return {string} The mfa method
 */
export const getMFAMethod = createSelector([getMFA], mfaState => _get(mfaState, 'method', ''));

/**
 * Get the MFA methods the user is allowed to enroll into.
 *
 * @method getMFAAllowedMethods
 * @return {Array} The allowed methods
 */
export const getMFAAllowedMethods = createSelector([getMFA], mfaState =>
    _get(mfaState, 'allowedMethods', [])
);

/**
 * Get the `status` returned by the authenticate API.
 *
 * @method getMFAStatus
 * @return {string} The mfa status
 */
export const getMFAStatus = createSelector([getMFA], mfaState => _get(mfaState, 'status', ''));

/**
 * Get the login credentials kept for the post sign in redirect.
 *
 * @method getMFALoginContext
 * @return {Object} The login context
 */
export const getMFALoginContext = createSelector([getMFA], mfaState =>
    _get(mfaState, 'loginContext', {})
);

/**
 * Get the error of the last verification attempt.
 *
 * @method getMFAError
 * @return {Object|null} The error
 */
export const getMFAError = createSelector([getMFA], mfaState => _get(mfaState, 'error', null));

/**
 * Get the attempts left in the current challenge, as reported by the API.
 *
 * NOTE: the value outlives the error it came with, so the screen keeps showing
 * it once the error itself has been cleared.
 *
 * @method getMFARemainingAttempts
 * @return {number|null} The attempts left
 */
export const getMFARemainingAttempts = createSelector([getMFA], mfaState =>
    _get(mfaState, 'remainingAttempts', null)
);

/**
 * Determine whether a verification request is in flight.
 *
 * @method isVerifyingMFACode
 * @return {boolean}
 */
export const isVerifyingMFACode = createSelector([getMFA], mfaState =>
    Boolean(_get(mfaState, 'isVerifying', false))
);

/**
 * Get the error of the last enrollment attempt.
 *
 * @method getMFAEnrollmentError
 * @return {Object|null} The error
 */
export const getMFAEnrollmentError = createSelector([getMFA], mfaState =>
    _get(mfaState, 'enrollmentError', null)
);

/**
 * Get the `status` the register API answered the started enrollment with.
 *
 * @method getMFAEnrollmentStatus
 * @return {string} The enrollment status
 */
export const getMFAEnrollmentStatus = createSelector([getMFA], mfaState =>
    _get(mfaState, 'enrollmentStatus', '')
);

/**
 * Get the shared secret of the started enrollment.
 *
 * NOTE: the secret lives in this state alone, it is never persisted and must
 * never be logged or reported.
 *
 * @method getMFASecret
 * @return {string} The shared secret
 */
export const getMFASecret = createSelector([getMFA], mfaState => _get(mfaState, 'secret', ''));

/**
 * Get the `otpauth://` URI of the started enrollment.
 *
 * @method getMFAQRCode
 * @return {string} The QR code URI
 */
export const getMFAQRCode = createSelector([getMFA], mfaState => _get(mfaState, 'qrCode', ''));

/**
 * Determine whether an enrollment request is in flight.
 *
 * @method isRegisteringMFAMethod
 * @return {boolean}
 */
export const isRegisteringMFAMethod = createSelector([getMFA], mfaState =>
    Boolean(_get(mfaState, 'isRegistering', false))
);

/**
 * Determine whether an enrollment has just been completed, i.e. whether the
 * enrollment success screen has something to confirm.
 *
 * @method isMFAEnrollmentComplete
 * @return {boolean}
 */
export const isMFAEnrollmentComplete = createSelector([getMFA], mfaState =>
    Boolean(_get(mfaState, 'isEnrollmentComplete', false))
);

/**
 * Determine whether there is an MFA session waiting to be completed.
 *
 * @method hasPendingMFASession
 * @return {boolean}
 */
export const hasPendingMFASession = createSelector([getMFAToken], Boolean);

/**
 * Get the OAuth token set of the completed enrollment, held until the sign in
 * is completed from the success screen.
 *
 * @method getMFAPendingToken
 * @param  {Object}            state The app state
 * @return {Object|null}             The token set
 */
export const getMFAPendingToken = createSelector([getMFA], mfaState =>
    _get(mfaState, 'pendingToken', null)
);

/**
 * Is the sign in of the completed enrollment in flight?
 *
 * @method isCompletingMFAEnrollment
 * @param  {Object}                  state The app state
 * @return {boolean}
 */
export const isCompletingMFAEnrollment = createSelector([getMFA], mfaState =>
    Boolean(_get(mfaState, 'isCompletingEnrollment', false))
);
